// src/pages/maintenance/PmSchedulePage.js
// Preventive maintenance templates, sorted by next due date — create/edit, and a manual
// "generate now" escape hatch for templates the scheduler hasn't picked up yet.
// See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import ConfirmModal from '../../components/ConfirmModal';
import MaintenanceNav from '../../components/MaintenanceNav';
import PmTemplateModal from './PmTemplateModal';

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function PmSchedulePage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await maintenanceService.getPmTemplates();
      setTemplates(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = () => { setShowModal(false); setEditing(null); load(); };

  const handleArchive = async () => {
    await maintenanceService.deletePmTemplate(archiveTarget.id);
    setArchiveTarget(null);
    load();
  };

  const generateNow = async (template) => {
    try {
      const wo = await maintenanceService.generatePmNow(template.id);
      navigate(`/maintenance/work-orders/${wo.id}`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to generate work order.');
    }
  };

  const isDueSoon = (t) => t.nextDueAt && new Date(t.nextDueAt) <= new Date(Date.now() + (t.leadTimeDays || 0) * 86400000);

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Preventive Maintenance Schedule</h4>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowModal(true); }}>+ New PM Template</button>
      </div>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-5 text-muted">No PM templates yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Title</th><th>Asset</th><th>Trigger</th><th>Next Due</th><th>Priority</th><th>Assigned</th><th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id}>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{t.title}</td>
                  <td style={{ fontSize: 13 }}>{t.Asset?.name || '—'}</td>
                  <td style={{ fontSize: 13 }}>
                    {t.triggerType === 'calendar' ? `Every ${t.calendarIntervalDays} days` : `Every ${t.meterThreshold} units`}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {t.nextDueAt ? new Date(t.nextDueAt).toLocaleDateString() : '—'}
                    {isDueSoon(t) && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>Due Soon</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>{labelize(t.priority)}</td>
                  <td style={{ fontSize: 13 }}>
                    {t.DefaultAssignee
                      ? (t.DefaultAssignee.username || `${t.DefaultAssignee.firstName || ''} ${t.DefaultAssignee.lastName || ''}`.trim())
                      : <span className="text-muted">Unassigned</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div className="d-flex justify-content-end gap-1">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => generateNow(t)}>Generate WO Now</button>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditing(t); setShowModal(true); }}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setArchiveTarget(t)}>Archive</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <PmTemplateModal template={editing} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} />
      )}

      <ConfirmModal
        show={!!archiveTarget}
        title="Archive PM Template"
        message={`Archive "${archiveTarget?.title}"? It will stop generating work orders.`}
        confirmLabel="Archive"
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />
      </div>
    </>
  );
}
